import type { LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData, Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { BreadcrumbSchema, FAQSchema } from '~/components/structured-data'
import { STATIC_PAGE_HEADERS } from '~/lib/cache.server'
import { CEE_OPERATIONS } from '~/lib/cee-data.server'
import { CATEGORY_LABELS } from '~/lib/cee-types'
import {
  ArrowRight, ArrowLeft, CheckCircle, AlertTriangle, Sparkles, Tag,
  Shield, Award, Zap, FileText, HelpCircle,
} from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export async function loader({ params }: LoaderFunctionArgs) {
  const op = CEE_OPERATIONS.find((o) => o.slug === params.slug)
  if (!op) throw new Response('Not Found', { status: 404 })
  return { op }
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  if (!data) return []
  const op = data.op
  return [
    { title: `${op.name} — Prime CEE ${op.montantClassique} | AfficheServices` },
    { name: 'description', content: `${op.shortDesc} Prime CEE ${op.montantClassique}, jusqu'à ${op.montantPrecarite} pour les ménages modestes. Conditions, cumul MaPrimeRénov', artisans RGE.` },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/cee/${op.slug}` },
    { property: 'og:title', content: `${op.name} — Prime CEE ${op.montantClassique} | AfficheServices` },
    { property: 'og:description', content: `${op.shortDesc} Prime CEE ${op.montantClassique}.` },
    { property: 'og:url', content: `https://www.afficheservices.fr/cee/${op.slug}` },
  ]
}

export default function CEEOperationPage() {
  const { op } = useLoaderData<typeof loader>()

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'CEE', url: 'https://www.afficheservices.fr/cee' },
        { name: op.name, url: `https://www.afficheservices.fr/cee/${op.slug}` },
      ]} />
      {op.faq.length > 0 && <FAQSchema items={op.faq.map((f) => ({ question: f.q, answer: f.a }))} />}

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 via-indigo-900 to-violet-900 px-4 py-12 text-white sm:py-16">
        <div className="mx-auto max-w-4xl">
          <Link to="/cee" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-200 hover:text-white">
            <ArrowLeft className="h-3.5 w-3.5" /> Catalogue CEE
          </Link>
          <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-mono text-blue-200">
            <Tag className="h-3 w-3" /> {op.code}
          </div>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-4xl">{op.name}</h1>
          <p className="mt-3 max-w-2xl text-base text-blue-200">{op.shortDesc}</p>

          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-white/10 p-4 ring-1 ring-white/20">
              <p className="text-xs font-semibold uppercase tracking-wide text-blue-200">Prime classique</p>
              <p className="mt-1 text-2xl font-bold text-white">{op.montantClassique}</p>
            </div>
            <div className="rounded-xl bg-emerald-500/20 p-4 ring-1 ring-emerald-300/30">
              <p className="text-xs font-semibold uppercase tracking-wide text-emerald-200">Bonus précarité</p>
              <p className="mt-1 text-2xl font-bold text-emerald-100">{op.montantPrecarite}</p>
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto grid max-w-5xl gap-8 px-4 py-12 lg:grid-cols-3 lg:px-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-10">
          <section>
            <h2 className="text-xl font-bold text-slate-900">En quoi ça consiste ?</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-600">{op.description}</p>
          </section>

          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <CheckCircle className="h-5 w-5 text-emerald-500" /> Conditions d'éligibilité
            </h2>
            <ul className="mt-4 space-y-2">
              {op.conditions.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {c}
                </li>
              ))}
            </ul>
          </section>

          <section>
            <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
              <Sparkles className="h-5 w-5 text-primary" /> Avantages
            </h2>
            <ul className="mt-4 space-y-2">
              {op.avantages.map((a, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {a}
                </li>
              ))}
            </ul>
          </section>

          <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6">
            <h2 className="flex items-center gap-2 text-xl font-bold text-amber-900">
              <AlertTriangle className="h-5 w-5" /> Pièges à éviter
            </h2>
            <ul className="mt-4 space-y-2">
              {op.pieges.map((p, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-amber-800">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" /> {p}
                </li>
              ))}
            </ul>
          </section>

          {op.aiClaims && op.aiClaims.length > 0 && (
            <section className="rounded-2xl border border-indigo-200 bg-indigo-50 p-6">
              <h2 className="flex items-center gap-2 text-xl font-bold text-indigo-900">
                📊 En chiffres
              </h2>
              <ul className="mt-4 space-y-2">
                {op.aiClaims.map((claim, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm leading-relaxed text-indigo-900">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-600" />
                    {claim}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {op.faq.length > 0 && (
            <section>
              <h2 className="flex items-center gap-2 text-xl font-bold text-slate-900">
                <HelpCircle className="h-5 w-5 text-primary" /> Questions fréquentes
              </h2>
              <div className="mt-4 space-y-3">
                {op.faq.map((item, i) => (
                  <details key={i} className="group rounded-xl border border-border bg-white p-4">
                    <summary className="flex cursor-pointer items-center justify-between text-sm font-semibold text-slate-900">
                      {item.q}
                      <ArrowRight className="h-4 w-4 transition-transform group-open:rotate-90" />
                    </summary>
                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{item.a}</p>
                  </details>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <div className="sticky top-20 space-y-4">
            <div className="rounded-2xl border border-border bg-white p-6 shadow-md">
              <h3 className="text-sm font-bold text-slate-900">Infos clés</h3>
              <dl className="mt-4 space-y-3 text-xs">
                <div>
                  <dt className="flex items-center gap-1 text-slate-500">
                    <Tag className="h-3 w-3" /> Code
                  </dt>
                  <dd className="mt-0.5 font-mono font-semibold text-slate-900">{op.code}</dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1 text-slate-500">
                    <Zap className="h-3 w-3" /> Exigence technique
                  </dt>
                  <dd className="mt-0.5 font-semibold text-slate-900">{op.requirement}</dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1 text-slate-500">
                    <Award className="h-3 w-3" /> Certification RGE
                  </dt>
                  <dd className="mt-0.5 font-semibold text-slate-900">{op.certification}</dd>
                </div>
                <div>
                  <dt className="flex items-center gap-1 text-slate-500">
                    <Shield className="h-3 w-3" /> Catégorie
                  </dt>
                  <dd className="mt-0.5 font-semibold text-slate-900">{CATEGORY_LABELS[op.category]}</dd>
                </div>
              </dl>

              <Link
                to="/devis-gratuit"
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-semibold text-white hover:bg-primary-hover"
              >
                Recevoir 3 devis gratuits <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/simulateur-aides"
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-white px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Simuler mes aides
              </Link>
            </div>

            <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5">
              <p className="flex items-center gap-2 text-xs font-semibold text-indigo-700">
                <FileText className="h-4 w-4" /> En savoir plus
              </p>
              <ul className="mt-3 space-y-2 text-xs">
                <li>
                  <Link to="/cee/guides/cumul-maprimerenov-cee" className="text-indigo-700 hover:underline">
                    → Cumul MaPrimeRénov' + CEE
                  </Link>
                </li>
                <li>
                  <Link to="/cee/guides/ordre-chronologique-cee" className="text-indigo-700 hover:underline">
                    → Ordre chronologique CEE
                  </Link>
                </li>
                <li>
                  <Link to="/cee/guides/fraude-rge-comment-verifier" className="text-indigo-700 hover:underline">
                    → Vérifier un artisan RGE
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </aside>
      </div>

      <Footer />
    </div>
  )
}
